import {
  PhaseStructureTypeEnum,
  SUBDIVISION_EXTERIOR_PAYOUT_OFFSET_MIN,
} from '@vatos-pas/common';

export interface CostFieldConfig {
  source: string;
  label: string;
  min: number;
  max?: number;
  step: number;
  startNewRow?: boolean;
  requiredWhenFixed?: boolean;
  offsetMin?: number;
  deps?: string;
}

export interface WorkflowFieldConfig {
  production: CostFieldConfig[];
  warranty: CostFieldConfig[];
  additional: CostFieldConfig[];
}

export const DRYWALL_FIELDS: WorkflowFieldConfig = {
  production: [
    {
      source: 'dwHangingCostPerBoard',
      label: 'Hang (Per Board)',
      min: 1,
      max: 100,
      step: 0.01,
    },
    {
      source: 'dwFinishingCostPerBoard',
      label: 'Finish (Per Board)',
      min: 1,
      max: 100,
      step: 0.01,
    },
    {
      source: 'dwSprayingCostPerBoard',
      label: 'Spray (Per Board)',
      min: 1,
      max: 100,
      step: 0.01,
    },
  ],
  warranty: [
    {
      source: 'pieceworkPercentage',
      label: 'Piecework (Percentage)',
      min: 0,
      max: 100,
      step: 0.01,
    },
    {
      source: 'specialJobsPercentage',
      label: 'Special Jobs (Percentage)',
      min: 0,
      max: 100,
      step: 0.01,
    },
  ],
  additional: [
    {
      source: 'garageCostFixed',
      label: 'Garage (Fixed Cost)',
      min: 1,
      max: 100,
      step: 0.01,
    },
    {
      source: 'highCeilingPerBoard',
      label: 'High Ceiling (Per Board)',
      min: 0,
      step: 0.01,
    },
    {
      source: 'screwInspectionPerBoard',
      label: 'Screw Inspection (Per Board)',
      min: 0,
      step: 0.01,
    },
  ],
};

export const DRYWALL_AND_PAINT_FIELDS: WorkflowFieldConfig = {
  production: [
    {
      source: 'dwpHangingCostPerBoard',
      label: 'Hang (Per Board)',
      min: 1,
      max: 100,
      step: 0.01,
    },
    {
      source: 'dwpFinishCostPerBoard',
      label: 'Finish (Per Board)',
      min: 1,
      max: 100,
      step: 0.01,
    },
    {
      source: 'dwpPrimeCostPerSqft',
      label: 'Prime (Per Sq Ft)',
      min: 1,
      max: 100,
      step: 0.01,
    },
    {
      source: 'dwpTrim1CostPerSqft',
      label: 'Trim 1 (Per Sq Ft)',
      min: 1,
      max: 100,
      step: 0.01,
    },
    {
      source: 'dwpBump1CostPerBoard',
      label: 'Drywall Touch Up 1 (Per Board)',
      min: 1,
      max: 100,
      step: 0.01,
    },
    {
      source: 'dwpInteriorPaintCostPerSqft',
      label: 'Interior Paint (Per Sq Ft)',
      min: 1,
      max: 100,
      step: 0.01,
    },
    {
      source: 'dwpTrim2CostPerSqft',
      label: 'Trim 2 (Per Sq Ft)',
      min: 1,
      max: 100,
      step: 0.01,
    },
    {
      source: 'dwpBump2CostPerBoard',
      label: 'Drywall Touch Up 2 (Per Board)',
      min: 1,
      max: 100,
      step: 0.01,
    },
    {
      source: 'dwpFinalPaintCostPerSqft',
      label: 'Final Paint (Per Sq Ft)',
      min: 1,
      max: 100,
      step: 0.01,
    },
    {
      source: 'dwpBump3CostPerBoard',
      label: 'Drywall Touch Up 3 (Per Board)',
      min: 1,
      max: 100,
      step: 0.01,
    },
    {
      source: 'dwpCMBluetapeCostPerSqft',
      label: 'CM Bluetape (Per Sq Ft)',
      min: 1,
      max: 100,
      step: 0.01,
    },
    {
      source: 'dwpFinalBluetapeCostPerSqft',
      label: 'Final Bluetape (Per Sq Ft)',
      min: 1,
      max: 100,
      step: 0.01,
    },
    {
      source: 'exteriorOnePercentage',
      label: 'Exterior One (Percentage)',
      min: 0,
      max: 100,
      step: 0.01,
      startNewRow: true,
      requiredWhenFixed: true,
      offsetMin: SUBDIVISION_EXTERIOR_PAYOUT_OFFSET_MIN,
    },
    {
      source: 'exteriorTwoPercentage',
      label: 'Exterior Two (Percentage)',
      min: 0,
      max: 100,
      step: 0.01,
      requiredWhenFixed: true,
      offsetMin: SUBDIVISION_EXTERIOR_PAYOUT_OFFSET_MIN,
      deps: 'exteriorOnePercentage',
    },
  ],
  warranty: [
    {
      source: 'pieceworkPercentage',
      label: 'Piecework (Percentage)',
      min: 0,
      max: 100,
      step: 0.01,
    },
    {
      source: 'specialJobsPercentage',
      label: 'Special Jobs (Percentage)',
      min: 0,
      max: 100,
      step: 0.01,
    },
  ],
  additional: [
    {
      source: 'garageCostFixed',
      label: 'Garage (Fixed Cost)',
      min: 1,
      max: 100,
      step: 0.01,
    },
    {
      source: 'highCeilingPerBoard',
      label: 'High Ceiling (Per Board)',
      min: 0,
      step: 0.01,
    },
    {
      source: 'screwInspectionPerBoard',
      label: 'Screw Inspection (Per Board)',
      min: 0,
      step: 0.01,
    },
    {
      source: 'interiorOptionsPercentage',
      label: 'Paint Int. (Percentage)',
      min: 0,
      max: 100,
      step: 0.01,
      requiredWhenFixed: true,
    },
    {
      source: 'exteriorOptionsPercentage',
      label: 'Paint Ext. (Percentage)',
      min: 0,
      max: 100,
      step: 0.01,
      requiredWhenFixed: true,
    },
    {
      source: 'trimOptionPercentage',
      label: 'Trim Option (Percentage)',
      min: 0,
      max: 100,
      step: 0.01,
      startNewRow: true,
      requiredWhenFixed: true,
    },
  ],
};

export const getFieldsForWorkflow = (
  phaseStructureType: PhaseStructureTypeEnum,
): WorkflowFieldConfig => {
  return phaseStructureType === PhaseStructureTypeEnum.DrywallAndPaint
    ? DRYWALL_AND_PAINT_FIELDS
    : DRYWALL_FIELDS;
};

export const getAllCostFieldSources = (): string[] => {
  const allFields = [
    ...DRYWALL_FIELDS.production,
    ...DRYWALL_FIELDS.warranty,
    ...DRYWALL_FIELDS.additional,
    ...DRYWALL_AND_PAINT_FIELDS.production,
    ...DRYWALL_AND_PAINT_FIELDS.warranty,
    ...DRYWALL_AND_PAINT_FIELDS.additional,
  ];
  return [...new Set(allFields.map(f => f.source))];
};

export const mapCostFields = (
  values: Record<string, any>,
): Record<string, any> =>
  Object.fromEntries(
    getAllCostFieldSources().map(key => [key, values[key] ?? null]),
  );
